/**
 * Clear pending mapping datasets when a BOOLEAN mapping_parent is OFF (UI-hidden nest).
 * v1: mapping_parent / parent only (not belongs_to_id).
 */

import {
  getMappingOwnId,
  isBooleanMapping,
  isTruthyMappingParentValue,
  toFiniteMappingId,
} from './mappingParentGroup'
import {
  clearFieldMappingPendingEntry,
} from './settingControlVisibilityPersistence'

export type NestedMappingParentSyncPair = {
  thisSideMappings: any[]
  oppositeSideMappings: any[]
}

const hasMappingDatasetValues = (entry: any): boolean => {
  const rows = entry?.fieldMappingDataSet ?? entry?.fieldmappingdata_set ?? []
  if (!Array.isArray(rows) || !rows.length) {
    return false
  }
  return rows.some((row) => {
    const dest = row?.destination_platform_data_id ?? row?.destination_platform_data_str
    const source = row?.source_platform_data_id ?? row?.source_platform_data_str
    return (
      (dest !== null && dest !== undefined && `${dest}`.trim() !== '') ||
      (source !== null && source !== undefined && `${source}`.trim() !== '')
    )
  })
}

/** v1: declared nest via mapping_parent/parent only; belongs_to_id is v2. */
export const getDeclaredNestedMappingParentId = (
  catalogRow: {belongs_to_id?: unknown; mapping_parent?: unknown; parent?: unknown} | null | undefined
): number | null => {
  if (toFiniteMappingId(catalogRow?.belongs_to_id) !== null) {
    return null
  }
  return (
    toFiniteMappingId(catalogRow?.mapping_parent) ?? toFiniteMappingId(catalogRow?.parent)
  )
}

const collectCatalogMappings = (pairs: NestedMappingParentSyncPair[]): any[] => {
  const seen = new Set<number>()
  const rows: any[] = []
  pairs.forEach((pair) => {
    ;[...(pair.thisSideMappings ?? []), ...(pair.oppositeSideMappings ?? [])].forEach((row) => {
      const ownId = getMappingOwnId(row)
      if (ownId === null || seen.has(ownId)) {
        return
      }
      seen.add(ownId)
      rows.push(row)
    })
  })
  return rows
}

const buildBooleanMappingParentIds = (catalogRows: any[]): Set<number> => {
  const ids = new Set<number>()
  catalogRows.forEach((row) => {
    const ownId = getMappingOwnId(row)
    if (ownId !== null && isBooleanMapping(row)) {
      ids.add(ownId)
    }
  })
  return ids
}

const findCatalogRowByPendingId = (
  settingMappingId: number,
  pairs: NestedMappingParentSyncPair[]
): any | null => {
  for (const pair of pairs) {
    const all = [...(pair.thisSideMappings ?? []), ...(pair.oppositeSideMappings ?? [])]
    const row = all.find(
      (r) => r?.setting_mapping_id === settingMappingId || r?.id === settingMappingId
    )
    if (row) {
      return row
    }
  }
  return null
}

export const isPendingFieldMappingHiddenByMappingParentOff = (
  catalogRow: any,
  booleanParentIds: Set<number>,
  resolveMappingValue: (settingMappingId: number) => unknown
): boolean => {
  const parentId = getDeclaredNestedMappingParentId(catalogRow)
  if (parentId === null || !booleanParentIds.has(parentId)) {
    return false
  }
  return !isTruthyMappingParentValue(resolveMappingValue(parentId))
}

export const pruneFieldMappingsHiddenByMappingParentOff = (
  fieldMappings: any[],
  pairs: NestedMappingParentSyncPair[],
  resolveMappingValue: (settingMappingId: number) => unknown
): any[] => {
  if (!fieldMappings?.length || !pairs.length) {
    return fieldMappings
  }

  const catalogRows = collectCatalogMappings(pairs)
  const booleanParentIds = buildBooleanMappingParentIds(catalogRows)
  if (!booleanParentIds.size) {
    return fieldMappings
  }

  let changed = false
  const next = fieldMappings.map((entry) => {
    const settingMappingId = entry?.setting_mapping_id
    if (settingMappingId === null || settingMappingId === undefined) {
      return entry
    }

    const catalogRow = findCatalogRowByPendingId(Number(settingMappingId), pairs)
    if (!catalogRow) {
      return entry
    }

    if (
      isPendingFieldMappingHiddenByMappingParentOff(
        catalogRow,
        booleanParentIds,
        resolveMappingValue
      ) &&
      hasMappingDatasetValues(entry)
    ) {
      changed = true
      return clearFieldMappingPendingEntry(entry)
    }

    return entry
  })

  return changed ? next : fieldMappings
}
